import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router'
import { useSessionStorage } from 'react-use'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { SESSION_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { useProcedureActions } from 'features/Procedure/hooks/useProcedureActions'
import { useFetchEncounterPatientsQuery } from 'features/Procedure/state/procedure.api'
import {
  getFirstEncounterUuid,
  mapEncountersToOptions,
  mapPatientsToOptions,
  mapEncounterDataToPatientEncounter,
  createPatientWithEncounters,
  resolveFullPatient,
  filterPatientOptionsByCancellation,
  isCanceledPatientAppointment,
  type EncounterSelectOption,
  type PatientSelectOption,
} from '../components/patient-encounter-select/patient-encounter-select.utils'
import { useLazyFetchPatientByIdQuery } from 'features/Patients/state/api/patientApi'
import {
  transactionsMappingApi,
  useLazyFetchPatientEncountersQuery,
} from 'features/Profile/features/TransactionsMapping/transactionsMapping.api'
import { SCHEDULE_TAGS, scheduleApi } from 'features/Schedule/api/schedule.api'
import useNavigateParams from 'common/hooks/useNavigateParams'
import type {
  Patient,
  PatientEncounter,
  PatientWithEncounters,
} from 'features/Procedure/state/procedure-api.type'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'
import type { IPatient } from 'features/Patients/interfaces/IPatient'
import { useProcedurePatientStatusPusher } from 'features/Procedure/hooks/useProcedurePatientStatusPusher'

type UsePatientEncounterSelectReturn = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  patientOptions: PatientSelectOption[]
  encounterOptions: EncounterSelectOption[]
  encounters: PatientEncounter[]
  isLoading: boolean
  isPatientSearchModalOpen: boolean
  patientInfo: Patient | null
  isEncounterFlashing: boolean
  newEncounterUids: Set<string>
  onPatientChange: (patientId: string) => void
  onEncounterChange: (encounterUid: string) => void
  onSelectPatientFromModal: (patient: IPatient) => void
  onOpenPatientSearchModal: () => void
  onClosePatientSearchModal: () => void
  onEncounterDropdownOpen: () => void
  hideCancellations: boolean
  onHideCancellationsChange: (hideCancellations: boolean) => void
}

type UsePatientEncounterSelectOptions = {
  executeVisitContextChange?: (applyChange: () => void | Promise<void>) => Promise<void>
}

const NEW_ENCOUNTER_HIGHLIGHT_CLEAR_DELAY_MS = 8000

export const usePatientEncounterSelect = (
  options?: UsePatientEncounterSelectOptions,
): UsePatientEncounterSelectReturn => {
  const { siteId } = useParams<{ siteId: string }>()

  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const navigateParams = useNavigateParams()
  const location = useLocation()

  const [additionalPatients, setAdditionalPatients] = useState<PatientWithEncounters[]>([])
  const [hideCancellations, setHideCancellations] = useSessionStorage<boolean>(
    SESSION_STORAGE_KEYS.HIDE_CANCELED_CHART_APPOINTMENTS,
    false,
  )
  const hideCancellationsRef = useRef(hideCancellations)
  hideCancellationsRef.current = hideCancellations
  const selectedPatientId = useAppSelector((state) => state.procedureReducer.selectedPatientId)
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )

  const [isVerifyingUrlPatient, setIsVerifyingUrlPatient] = useState(false)
  const [isPatientSearchModalOpen, setIsPatientSearchModalOpen] = useState(false)
  const [isEncounterFlashing, setIsEncounterFlashing] = useState(false)
  const [newEncounterUids, setNewEncounterUids] = useState<Set<string>>(new Set())

  const {
    handleSetSelectedComparisonEncounterUid,
    handleSetSelectedEncounterUid,
    handleSetSelectedPatientId,
    handleSetSelectedProcedure,
  } = useProcedureActions()
  const { handleSetModelVersion, handleSetDrawMode } = useSceneActions()

  const {
    data,
    isFetching,
    isLoading: isPatientsInitialLoading,
  } = useFetchEncounterPatientsQuery(
    { siteId: siteId! },
    {
      skip: !siteId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: true,
    },
  )

  const [
    fetchEncountersQuery,
    {
      data: encountersData,
      isFetching: isFetchingEncounters,
      isLoading: isEncountersInitialLoading,
      isError: isEncountersError,
      originalArgs,
    },
  ] = useLazyFetchPatientEncountersQuery()

  const [fetchPatientById] = useLazyFetchPatientByIdQuery()
  const hasCheckedUrlPatient = useRef(false)
  const lastValidEncounterRef = useRef<string | null>(null)
  const deepLinkEncounterUidRef = useRef<string | null | undefined>(undefined)
  if (deepLinkEncounterUidRef.current === undefined) {
    deepLinkEncounterUidRef.current = searchParams.get('encounterId')
  }
  const hasResolvedDeepLinkEncounterRef = useRef(false)
  const knownEncounterOptionsRef = useRef<Record<string, EncounterSelectOption>>({})
  const selectedPatientIdRef = useRef<number | null>(selectedPatientId)
  const selectedEncounterUidRef = useRef<string | null>(selectedEncounterUid)
  const isSilentEncounterRefreshInFlightRef = useRef(false)
  const pendingSilentEncounterRefreshRef = useRef(false)
  const runSilentEncounterRefreshRef = useRef<() => void>(() => {})
  const preRefreshEncounterUidsRef = useRef<Set<string> | null>(null)
  const currentEncounterUidsRef = useRef<Set<string>>(new Set())
  const encounterHighlightClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearEncounterHighlightClearTimeout = useCallback(() => {
    if (encounterHighlightClearTimeoutRef.current) {
      clearTimeout(encounterHighlightClearTimeoutRef.current)
      encounterHighlightClearTimeoutRef.current = null
    }
  }, [])

  const clearEncounterHighlightState = useCallback(() => {
    clearEncounterHighlightClearTimeout()
    setIsEncounterFlashing(false)
    setNewEncounterUids(new Set())
  }, [clearEncounterHighlightClearTimeout])

  useEffect(() => {
    selectedPatientIdRef.current = selectedPatientId
  }, [selectedPatientId])

  useEffect(() => {
    selectedEncounterUidRef.current = selectedEncounterUid
    if (selectedEncounterUid) {
      lastValidEncounterRef.current = selectedEncounterUid
    }
  }, [selectedEncounterUid])

  const basePatients = useMemo(() => {
    return data?.patients || []
  }, [data?.patients])

  const allPatients = useMemo(() => {
    return [...basePatients, ...additionalPatients]
  }, [basePatients, additionalPatients])

  const patientOptions = useMemo(() => {
    return filterPatientOptionsByCancellation(
      mapPatientsToOptions(allPatients, data?.patient_statuses || []),
      hideCancellations,
      selectedPatientId,
    )
  }, [allPatients, data?.patient_statuses, hideCancellations, selectedPatientId])

  const patientInfo = useMemo(() => {
    return allPatients.find((p) => p.patient.patient_id === selectedPatientId)?.patient || null
  }, [allPatients, selectedPatientId])

  const encounters = useMemo(() => {
    if (!encountersData?.encounters?.data) return []
    return mapEncounterDataToPatientEncounter(encountersData.encounters.data)
  }, [encountersData])

  const actualEncounterOptions = useMemo(() => {
    if (encounters.length === 0) return []
    return mapEncountersToOptions(encounters)
  }, [encounters])

  useEffect(() => {
    actualEncounterOptions.forEach((option) => {
      if (typeof option.code === 'string' && option.code.length > 0) {
        knownEncounterOptionsRef.current[option.code] = option
      }
    })
  }, [actualEncounterOptions])

  const encounterOptions = useMemo(() => {
    if (!selectedEncounterUid) {
      return actualEncounterOptions
    }

    const selectedEncounterExists = actualEncounterOptions.some(
      (option) => option.code === selectedEncounterUid,
    )

    if (selectedEncounterExists) {
      return actualEncounterOptions
    }

    const stickySelectedEncounterOption = knownEncounterOptionsRef.current[selectedEncounterUid]

    if (!stickySelectedEncounterOption) {
      return actualEncounterOptions
    }

    return [stickySelectedEncounterOption, ...actualEncounterOptions]
  }, [actualEncounterOptions, selectedEncounterUid])

  useEffect(() => {
    currentEncounterUidsRef.current = new Set(encounterOptions.map((opt) => opt.code as string))
  }, [encounterOptions])

  const fetchEncountersAndSetFirst = useCallback(
    async (patientId: number, preferredEncounterId?: string | null) => {
      handleSetSelectedEncounterUid(null)
      handleSetSelectedComparisonEncounterUid(null)
      handleSetSelectedPatientId(patientId)

      try {
        const result = await fetchEncountersQuery({ patientId }).unwrap()

        const fetchedEncounters = result?.encounters?.data
          ? mapEncounterDataToPatientEncounter(result.encounters.data)
          : []

        if (fetchedEncounters.length === 0) return

        if (preferredEncounterId) {
          const encounterExists = fetchedEncounters.some(
            (e) => e.encounter_uuid === preferredEncounterId,
          )
          if (encounterExists) {
            handleSetSelectedEncounterUid(preferredEncounterId)
            return
          }
        }

        const firstEncounter = getFirstEncounterUuid({
          patient: {} as any,
          encounters: fetchedEncounters,
        })
        if (firstEncounter) {
          handleSetSelectedEncounterUid(firstEncounter)
        }
      } catch (error) {
        console.error('Error fetching encounters:', error)
      }
    },
    [
      fetchEncountersQuery,
      handleSetSelectedComparisonEncounterUid,
      handleSetSelectedEncounterUid,
      handleSetSelectedPatientId,
    ],
  )

  const runSilentEncounterRefresh = useCallback(async () => {
    const patientId = selectedPatientIdRef.current
    if (!patientId) {
      return
    }

    isSilentEncounterRefreshInFlightRef.current = true

    if (preRefreshEncounterUidsRef.current === null) {
      preRefreshEncounterUidsRef.current = new Set(currentEncounterUidsRef.current)
    }

    try {
      const result = await dispatch(
        transactionsMappingApi.endpoints.fetchPatientEncounters.initiate(
          { patientId },
          { forceRefetch: true, subscribe: false },
        ),
      ).unwrap()
      const fetchedEncounters = result?.encounters?.data
        ? mapEncounterDataToPatientEncounter(result.encounters.data)
        : []

      if (selectedPatientIdRef.current !== patientId) {
        return
      }

      const previousUids = preRefreshEncounterUidsRef.current
      preRefreshEncounterUidsRef.current = null

      if (previousUids) {
        const fetchedUids = new Set(fetchedEncounters.map((e) => e.encounter_uuid))
        const detectedNewUids = new Set([...fetchedUids].filter((uid) => !previousUids.has(uid)))
        if (detectedNewUids.size > 0) {
          clearEncounterHighlightClearTimeout()
          setNewEncounterUids(detectedNewUids)
          setIsEncounterFlashing(true)
          dispatch(
            scheduleApi.util.invalidateTags([SCHEDULE_TAGS.SCHEDULE, SCHEDULE_TAGS.SCHEDULE_WEEK]),
          )
        }
      }

      const currentEncounterUid = selectedEncounterUidRef.current
      if (fetchedEncounters.length === 0) {
        return
      }

      if (
        currentEncounterUid &&
        fetchedEncounters.some((encounter) => encounter.encounter_uuid === currentEncounterUid)
      ) {
        return
      }

      if (currentEncounterUid) {
        return
      }

      const firstEncounter = getFirstEncounterUuid({
        patient: {} as any,
        encounters: fetchedEncounters,
      })

      if (firstEncounter !== currentEncounterUid) {
        handleSetSelectedEncounterUid(firstEncounter)
      }
    } catch (error) {
      console.error('Error refreshing encounters silently:', error)
    } finally {
      isSilentEncounterRefreshInFlightRef.current = false
      preRefreshEncounterUidsRef.current = null

      if (pendingSilentEncounterRefreshRef.current) {
        pendingSilentEncounterRefreshRef.current = false
        runSilentEncounterRefreshRef.current()
      }
    }
  }, [clearEncounterHighlightClearTimeout, dispatch, handleSetSelectedEncounterUid])

  useEffect(() => {
    runSilentEncounterRefreshRef.current = runSilentEncounterRefresh
  }, [runSilentEncounterRefresh])

  const requestSilentEncounterRefresh = useCallback(() => {
    if (!selectedPatientIdRef.current) {
      return
    }

    if (isSilentEncounterRefreshInFlightRef.current) {
      pendingSilentEncounterRefreshRef.current = true
      return
    }

    runSilentEncounterRefresh()
  }, [runSilentEncounterRefresh])

  useProcedurePatientStatusPusher(siteId, {
    selectedPatientId,
    onSelectedPatientEvent: requestSilentEncounterRefresh,
  })

  useEffect(() => {
    const handleWindowFocus = () => {
      requestSilentEncounterRefresh()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleWindowFocus()
      }
    }

    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [requestSilentEncounterRefresh])

  const executeVisitContextChange = useCallback(
    async (applyChange: () => void | Promise<void>) => {
      if (options?.executeVisitContextChange) {
        await options.executeVisitContextChange(applyChange)
        return
      }

      await applyChange()
    },
    [options?.executeVisitContextChange],
  )

  const isLoading = isPatientsInitialLoading || isEncountersInitialLoading || isVerifyingUrlPatient

  const hasAppLevelError =
    (encountersData?.encounters as any)?.error && originalArgs?.patientId === selectedPatientId
  const hasNetworkError = isEncountersError && originalArgs?.patientId === selectedPatientId

  useEffect(() => {
    if (selectedPatientId) {
      const currentPid = searchParams.get('id')
      const currentEncounterId = searchParams.get('encounterId')
      const selectedPatientStr = selectedPatientId.toString()

      const shouldUpdate =
        currentPid !== selectedPatientStr ||
        (selectedEncounterUid
          ? currentEncounterId !== selectedEncounterUid
          : !!currentEncounterId && !lastValidEncounterRef.current)

      if (shouldUpdate) {
        navigateParams(location.pathname, {
          id: selectedPatientStr,
          ...(selectedEncounterUid && { encounterId: selectedEncounterUid }),
        })
      }
    }
  }, [selectedPatientId, selectedEncounterUid, searchParams, navigateParams, location.pathname])

  useEffect(() => {
    if (hasResolvedDeepLinkEncounterRef.current) {
      return
    }

    const targetEncounterUid = deepLinkEncounterUidRef.current
    if (!targetEncounterUid) {
      hasResolvedDeepLinkEncounterRef.current = true
      return
    }

    const targetPatientId = searchParams.get('id')
    if (targetPatientId && String(selectedPatientId) !== targetPatientId) {
      return
    }

    if (encounters.length === 0) {
      return
    }

    const matchedEncounter = encounters.find(
      (encounter) => encounter.encounter_uuid === targetEncounterUid,
    )

    if (matchedEncounter) {
      if (selectedEncounterUid !== matchedEncounter.encounter_uuid) {
        handleSetSelectedEncounterUid(matchedEncounter.encounter_uuid)
      }
      hasResolvedDeepLinkEncounterRef.current = true
    }
  }, [
    encounters,
    selectedPatientId,
    selectedEncounterUid,
    searchParams,
    handleSetSelectedEncounterUid,
  ])

  useEffect(() => {
    if (selectedPatientId && (encounters.length > 0 || selectedEncounterUid)) return
    if (
      isFetching ||
      isVerifyingUrlPatient ||
      isFetchingEncounters ||
      hasAppLevelError ||
      hasNetworkError
    ) {
      return
    }

    if (selectedPatientId) {
      const encounterIdFromUrl = searchParams.get('encounterId')
      fetchEncountersAndSetFirst(selectedPatientId, encounterIdFromUrl)
      return
    }

    setIsVerifyingUrlPatient(true)

    const pidFromUrl = searchParams.get('id')
    const encounterIdFromUrl = searchParams.get('encounterId')

    const initializePatient = async () => {
      try {
        if (pidFromUrl) {
          const pidNumber = Number(pidFromUrl)
          const patientExistsInBase = basePatients.some((p) => p.patient.patient_id === pidNumber)

          if (patientExistsInBase) {
            await fetchEncountersAndSetFirst(pidNumber, encounterIdFromUrl)
            return
          }

          if (!hasCheckedUrlPatient.current) {
            hasCheckedUrlPatient.current = true
            try {
              const result = await fetchPatientById({ patientId: pidNumber }).unwrap()
              if (result) {
                const newPatient = createPatientWithEncounters(result, [])
                setAdditionalPatients((prev) => [...prev, newPatient])
                await fetchEncountersAndSetFirst(pidNumber, encounterIdFromUrl)
                return
              }
            } catch {
              // Ignore URL patient fetch errors and continue with fallback selection.
            }
          }
        }

        if (basePatients.length > 0) {
          const visiblePatients = hideCancellationsRef.current
            ? basePatients.filter((patient) => !isCanceledPatientAppointment(patient.patient))
            : basePatients
          const firstPatient = visiblePatients[0] ?? basePatients[0]
          await fetchEncountersAndSetFirst(firstPatient.patient.patient_id, encounterIdFromUrl)
        }
      } finally {
        setIsVerifyingUrlPatient(false)
      }
    }

    initializePatient()
  }, [
    basePatients,
    selectedPatientId,
    selectedEncounterUid,
    encounters.length,
    isFetching,
    isFetchingEncounters,
    isVerifyingUrlPatient,
    hasAppLevelError,
    hasNetworkError,
    searchParams,
    fetchEncountersAndSetFirst,
    fetchPatientById,
  ])

  const onEncounterDropdownOpen = useCallback(() => {
    if (!isEncounterFlashing && newEncounterUids.size === 0) {
      return
    }

    if (encounterHighlightClearTimeoutRef.current) {
      return
    }

    encounterHighlightClearTimeoutRef.current = setTimeout(() => {
      encounterHighlightClearTimeoutRef.current = null
      setIsEncounterFlashing(false)
      setNewEncounterUids(new Set())
    }, NEW_ENCOUNTER_HIGHLIGHT_CLEAR_DELAY_MS)
  }, [isEncounterFlashing, newEncounterUids])

  const onPatientChange = useCallback(
    async (patientId: string) => {
      const newPatientId = Number(patientId)
      if (!Number.isFinite(newPatientId) || newPatientId === selectedPatientIdRef.current) {
        return
      }

      await executeVisitContextChange(async () => {
        handleSetSelectedProcedure(null)
        handleSetDrawMode(null)
        clearEncounterHighlightState()
        await fetchEncountersAndSetFirst(newPatientId)
      })
    },
    [
      clearEncounterHighlightState,
      executeVisitContextChange,
      fetchEncountersAndSetFirst,
      handleSetDrawMode,
      handleSetSelectedProcedure,
    ],
  )

  const onEncounterChange = useCallback(
    async (encounterUid: string) => {
      if (!encounterUid || encounterUid === selectedEncounterUidRef.current) {
        return
      }

      await executeVisitContextChange(() => {
        handleSetSelectedEncounterUid(encounterUid)
        handleSetModelVersion(null)
        handleSetSelectedProcedure(null)
        handleSetDrawMode(null)
        clearEncounterHighlightState()
      })
    },
    [
      clearEncounterHighlightState,
      executeVisitContextChange,
      handleSetDrawMode,
      handleSetModelVersion,
      handleSetSelectedEncounterUid,
      handleSetSelectedProcedure,
    ],
  )

  const onSelectPatientFromModal = useCallback(
    async (patient: IPatient) => {
      await executeVisitContextChange(async () => {
        handleSetModelVersion(null)
        handleSetSelectedProcedure(null)
        handleSetDrawMode(null)
        clearEncounterHighlightState()
        setIsPatientSearchModalOpen(false)

        const patientAlreadyExists = allPatients.some((p) => p.patient.patient_id === patient.id)

        if (!patientAlreadyExists) {
          // Search rows omit contact details (discounts, referral source, email),
          // so hydrate from the full patient record before caching it locally.
          const fullPatient = await resolveFullPatient(patient, (patientId) =>
            fetchPatientById({ patientId }).unwrap(),
          )
          const newPatient = createPatientWithEncounters(fullPatient, [])
          setAdditionalPatients((prev) => [...prev, newPatient])
        }

        await fetchEncountersAndSetFirst(patient.id)
      })
    },
    [
      allPatients,
      clearEncounterHighlightState,
      executeVisitContextChange,
      fetchEncountersAndSetFirst,
      fetchPatientById,
      handleSetDrawMode,
      handleSetModelVersion,
      handleSetSelectedProcedure,
    ],
  )

  const onOpenPatientSearchModal = useCallback(() => {
    setIsPatientSearchModalOpen(true)
  }, [])

  const onClosePatientSearchModal = useCallback(() => {
    setIsPatientSearchModalOpen(false)
  }, [])

  useEffect(() => {
    return () => {
      clearEncounterHighlightClearTimeout()
    }
  }, [clearEncounterHighlightClearTimeout])

  return {
    selectedPatientId,
    selectedEncounterUid,
    patientOptions,
    encounterOptions,
    encounters,
    isLoading,
    patientInfo,
    isPatientSearchModalOpen,
    isEncounterFlashing,
    newEncounterUids,

    onPatientChange,
    onEncounterChange,
    onSelectPatientFromModal,
    onOpenPatientSearchModal,
    onClosePatientSearchModal,
    onEncounterDropdownOpen,
    hideCancellations: hideCancellations ?? false,
    onHideCancellationsChange: setHideCancellations,
  }
}
